import clsx from 'clsx';
import Badge from '../ui/Badge';
import { formatCurrency, formatDuration } from '../../lib/formatPlan';

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// state badges: amber=scheduled, green=executed, slate/red=cancelled —
// exact enum values confirmed against the live rollover history response
// (state is echoed back verbatim from the backend, not re-derived).
const STATE_META = {
  scheduled: { label: 'Scheduled', tone: 'warning', dot: 'bg-warning' },
  executed: { label: 'Executed', tone: 'success', dot: 'bg-accent' },
  cancelled: { label: 'Cancelled', tone: 'danger', dot: 'bg-danger' },
};

/**
 * v1.17.0 — Rollover History, same timeline treatment as PayoutTimeline:
 * server orders newest-sequence-first already, rendered as-is with no
 * client-side re-sort. Each row shows the before/after term comparison
 * (`previous` vs `new`) so an admin/investor can see exactly what changed
 * — duration, maturity date, total interest, payout periods — without
 * having to diff two separate investment snapshots by hand.
 */
export default function RolloverHistory({ rollovers }) {
  if (!rollovers || rollovers.length === 0) {
    return <p className="py-6 text-center text-foreground-muted">No rollovers recorded yet.</p>;
  }

  return (
    <ol className="relative border-l border-border ml-2 space-y-6 py-1">
      {rollovers.map((r) => {
        const meta = STATE_META[r.state] || { label: r.state || 'Unknown', tone: 'neutral', dot: 'bg-foreground-subtle' };
        const byName =
          r.state === 'cancelled'
            ? r.cancelledBy?.name
            : r.state === 'executed'
            ? r.executedBy?.name
            : r.requestedBy?.name;
        const whenAt = r.state === 'cancelled' ? r.cancelledAt : r.state === 'executed' ? r.executedAt : r.requestedAt;

        return (
          <li key={r.id} className="relative ml-5">
            <span className={clsx('absolute -left-[27px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-background', meta.dot)} />
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={meta.tone}>{meta.label}</Badge>
              <span className="text-sm font-semibold text-foreground">Term #{r.sequence}</span>
              <span className="text-xs text-foreground-subtle">{formatDateOnly(whenAt)}</span>
              {r.executedAutomatically && <span className="text-[11px] text-foreground-subtle">(auto)</span>}
              {r.cancelledAutomatically && <span className="text-[11px] text-foreground-subtle">(auto-cancelled)</span>}
            </div>

            {r.previous && r.new && (
              <div className="mt-2 grid grid-cols-2 gap-3 rounded-xl border border-border bg-background-elevated p-3 text-xs max-w-md">
                <div>
                  <p className="text-foreground-subtle uppercase tracking-wide mb-1">Previous term</p>
                  <p className="text-foreground-muted">{formatDuration(r.previous.durationInDays)}</p>
                  <p className="text-foreground-muted">Matured {formatDateOnly(r.previous.maturityDate)}</p>
                  <p className="text-foreground-muted">{formatCurrency(r.previous.totalInterest)} interest</p>
                </div>
                <div>
                  <p className="text-foreground-subtle uppercase tracking-wide mb-1">New term</p>
                  <p className="text-foreground-muted">{formatDuration(r.new.durationInDays)}</p>
                  <p className="text-foreground-muted">Matures {formatDateOnly(r.new.maturityDate)}</p>
                  <p className="text-foreground-muted">
                    {formatCurrency(r.new.totalInterest)} interest
                    {r.new.interestAdded != null && (
                      <span className="text-foreground-subtle"> (+{formatCurrency(r.new.interestAdded)})</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {r.note && <p className="text-xs text-foreground-muted mt-1.5">{r.note}</p>}
            {r.state === 'cancelled' && r.cancelReason && (
              <p className="text-xs text-danger/80 mt-1">Reason: {r.cancelReason}</p>
            )}
            {byName && <p className="text-[11px] text-foreground-subtle mt-0.5">By {byName}</p>}
          </li>
        );
      })}
    </ol>
  );
}
